/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import MapView, { Circle, Marker } from 'react-native-maps';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketButton } from '../../../shared/ticket/TicketButton';
import { formatMoney } from '../../../shared/format/public-api';
import {
  createDeliveryZone,
  updateDeliveryZone,
  deleteDeliveryZone,
  setDeliveryModes,
} from '../../infra/deliveryZonesRepo';
import { getCachedDeliveryZones } from '../../application/storefrontStatus';
import { VUELTO_RIDERS_AVAILABLE } from '../../domain/storeProfileTypes';
import { supabase } from '../../../shared/supabase/supabaseClient';
import type { DeliveryZoneRow } from '@shared/deliveryTypes';
import { canRenderNativeMapView } from '../../../shared/maps/canRenderNativeMapView';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast'
import { useOperationalConfirm } from '../../../shared/ui/useOperationalConfirm';

type Props = {
  branchId: string;
  orgId: string;
  ownDeliveryEnabled: boolean;
  vueltoRidersEnabled: boolean;
  onModesChange: (modes: { ownDeliveryEnabled: boolean; vueltoRidersEnabled: boolean }) => void;
  canEditZones?: boolean;
  canEditFees?: boolean;
  canToggle?: boolean;
};

type ZoneDraft = {
  name: string;
  zone_type: 'radius' | 'polygon';
  radius_meters: number;
  delivery_fee: number;
  min_order_amount: number;
  estimated_time_minutes: number;
};

const emptyDraft: ZoneDraft = {
  name: '',
  zone_type: 'radius',
  radius_meters: 1000,
  delivery_fee: 0,
  min_order_amount: 0,
  estimated_time_minutes: 30,
};

export function DeliveryZonesSection({
  branchId,
  orgId,
  ownDeliveryEnabled,
  vueltoRidersEnabled,
  onModesChange,
  canEditZones = true,
  canEditFees = true,
  canToggle = true,
}: Props) {
  const canMutateZones = canEditZones;
  const canMutateFees = canEditFees;
  const deliveryEnabled = ownDeliveryEnabled || vueltoRidersEnabled;
  // Riders oculto hasta que salga la app — si la sucursal ya lo tiene ON, se muestra para poder apagarlo.
  const showRidersMode = VUELTO_RIDERS_AVAILABLE || vueltoRidersEnabled;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const [zones, setZones] = useState<DeliveryZoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DeliveryZoneRow | null>(null);
  const [draft, setDraft] = useState<ZoneDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [branchLat, setBranchLat] = useState<number | null>(null);
  const [branchLng, setBranchLng] = useState<number | null>(null);
  const [mapLoadError, setMapLoadError] = useState(false);
  const [zonesLoadError, setZonesLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('branches')
          .select('latitude, longitude')
          .eq('id', branchId)
          .single();
        if (cancelled) return;
        if (error) {
          setMapLoadError(true);
          return;
        }
        if (data?.latitude && data?.longitude) {
          setBranchLat(data.latitude);
          setBranchLng(data.longitude);
        }
      } catch {
        if (!cancelled) setMapLoadError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  const loadZones = useCallback(async () => {
    setLoading(true);
    setZonesLoadError(false);
    try {
      const data = await getCachedDeliveryZones(branchId);
      setZones(data);
    } catch (err) {
      console.warn('[DeliveryZonesSection] loadZones', err);
      setZonesLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const persistModes = async (modes: {
    ownDeliveryEnabled: boolean;
    vueltoRidersEnabled: boolean;
  }) => {
    if (!canToggle) {
      showInfo('Sin permiso', 'No podés activar o desactivar el envío.');
      return;
    }
    try {
      await setDeliveryModes(branchId, modes);
      onModesChange(modes);
      showSuccess('Envío actualizado');
    } catch {
      showError('Error', 'No se pudo cambiar el envío.');
    }
  };

  const handleStartCreate = () => {
    if (!canMutateZones) {
      showInfo('Sin permiso', 'No podés editar las zonas.');
      return;
    }
    setEditing(null);
    setDraft(emptyDraft);
    setShowForm(true);
  };

  const handleStartEdit = (zone: DeliveryZoneRow) => {
    if (!canMutateZones && !canMutateFees) {
      showInfo('Sin permiso', 'No podés editar las zonas.');
      return;
    }
    setEditing(zone);
    setDraft({
      name: zone.name,
      zone_type: zone.zone_type,
      radius_meters: zone.radius_meters,
      delivery_fee: zone.delivery_fee,
      min_order_amount: zone.min_order_amount,
      estimated_time_minutes: zone.estimated_time_minutes,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!draft.name.trim()) {
      showInfo('Falta nombre', 'Ingresá un nombre para la zona.');
      return;
    }
    if (draft.zone_type === 'radius' && (!draft.radius_meters || draft.radius_meters <= 0)) {
      showInfo('Radio inválido', 'Ingresá un radio en metros mayor a 0.');
      return;
    }
    if (editing ? !(canMutateZones || canMutateFees) : !canMutateZones) {
      showInfo('Sin permiso', 'No podés guardar esta zona.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateDeliveryZone(editing.id, {
          name: draft.name.trim(),
          zoneType: draft.zone_type,
          radiusMeters: draft.radius_meters,
          deliveryFee: draft.delivery_fee,
          minOrderAmount: draft.min_order_amount,
          estimatedTimeMinutes: draft.estimated_time_minutes,
        });
      } else {
        await createDeliveryZone(branchId, orgId, {
          name: draft.name.trim(),
          zoneType: draft.zone_type,
          radiusMeters: draft.radius_meters,
          area: null,
          deliveryFee: draft.delivery_fee,
          minOrderAmount: draft.min_order_amount,
          estimatedTimeMinutes: draft.estimated_time_minutes,
          sortOrder: zones.length,
        });
      }
      setShowForm(false);
      await loadZones();
      showSuccess(editing ? 'Zona actualizada' : 'Zona creada');
    } catch (err) {
      showError('Error', 'No se pudo guardar la zona.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone: DeliveryZoneRow) => {
    if (!canMutateZones) {
      showInfo('Sin permiso', 'No podés eliminar zonas.');
      return;
    }
    const ok = await confirm({
      title: 'Eliminar zona',
      message: `¿Eliminar "${zone.name}"?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteDeliveryZone(zone.id);
      await loadZones();
      showSuccess('Zona eliminada', zone.name);
    } catch {
      showError('Error', 'No se pudo eliminar.');
    }
  };

  const mapRegion = branchLat && branchLng
    ? {
        latitude: branchLat,
        longitude: branchLng,
        latitudeDelta: 0.02,
        longitudeDelta: 0.02,
      }
    : null;

  return (
    <View style={styles.sectionStack} testID="delivery.zones.section">
      {/* ── Modos de envío ── */}
      <PaperSheet padded testID="delivery.modes.sheet">
        <View style={styles.modesStack}>
          <Text style={styles.sectionTitle}>Modos de envío</Text>

          <View style={styles.toggleRow}>
            <View style={styles.toggleCopy}>
              <Text style={styles.toggleLabel}>Envío del local</Text>
              <Text style={styles.toggleHint}>Lo lleva tu gente. Vos definís la tarifa plana.</Text>
            </View>
            <Switch
              value={ownDeliveryEnabled}
              disabled={!canToggle}
              onValueChange={(enabled) =>
                void persistModes({ ownDeliveryEnabled: enabled, vueltoRidersEnabled })
              }
              trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
              testID="delivery.toggle.own"
            />
          </View>

          {showRidersMode ? (
            <View style={styles.toggleRow}>
              <View style={styles.toggleCopy}>
                <Text style={styles.toggleLabel}>Repartidores independientes</Text>
                <Text style={styles.toggleHint}>
                  El precio sale según la distancia. Lo cobrás vos y le liquidás al repartidor.
                </Text>
              </View>
              <Switch
                value={vueltoRidersEnabled}
                disabled={!canToggle}
                onValueChange={(enabled) =>
                  void persistModes({ ownDeliveryEnabled, vueltoRidersEnabled: enabled })
                }
                trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                testID="delivery.toggle.riders"
              />
            </View>
          ) : null}

          {!deliveryEnabled ? (
            <Text style={styles.hintWarn}>
              {showRidersMode
                ? 'Activá al menos un envío para ofrecer delivery.'
                : 'Activá el envío del local si querés ofrecer delivery.'}
            </Text>
          ) : null}
        </View>
      </PaperSheet>

      {/* ── Zonas ── */}
      {deliveryEnabled ? (
        <PaperSheet padded testID="delivery.zones.sheet">
          <View style={styles.zonesStack}>
            <Text style={styles.sectionTitle}>Zonas</Text>
            <Text style={styles.sectionCaption}>
              {vueltoRidersEnabled
                ? 'Armá radio, pedido mínimo y cuánto tarda. Vale para los dos envíos. Sin zona no se valida la dirección.'
                : 'Armá radio, pedido mínimo y cuánto tarda. Sin zona no se valida la dirección.'}
            </Text>

            {loading ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={ticketColors.gold} />
                <Text style={styles.loadingText}>Cargando zonas…</Text>
              </View>
            ) : zonesLoadError ? (
              <View style={styles.errorRow}>
                <Text style={styles.errorText}>
                  No se pudieron cargar las zonas.
                </Text>
                <TouchableOpacity onPress={() => void loadZones()} testID="delivery.zones.retry">
                  <Text style={styles.retryBtn}>Reintentar</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <>
                {zones.length > 0 ? (
                  <View style={styles.zonesList}>
                    {zones.map((zone) => (
                      <View key={zone.id} style={styles.zoneCard} testID={`delivery.zone.${zone.id}`}>
                        <View style={styles.zoneInfo}>
                          <Text style={styles.zoneName}>{zone.name}</Text>
                          <Text style={styles.zoneMeta}>
                            {zone.zone_type === 'radius'
                              ? `Radio: ${zone.radius_meters} m`
                              : 'Polígono'}
                            {ownDeliveryEnabled
                              ? `  •  Tarifa local: ${formatMoney(zone.delivery_fee)}`
                              : ''}
                            {'  •  '}
                            Pedido mínimo: {formatMoney(zone.min_order_amount)}
                            {'  •  '}
                            Tarda: {zone.estimated_time_minutes} min
                          </Text>
                        </View>
                        <View style={styles.zoneActions}>
                          <TouchableOpacity
                            onPress={() => handleStartEdit(zone)}
                            testID={`delivery.zone.${zone.id}.edit`}
                          >
                            <Text style={styles.editBtn}>Editar</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => handleDelete(zone)}
                            testID={`delivery.zone.${zone.id}.delete`}
                          >
                            <Text style={styles.deleteBtn}>Eliminar</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : null}

                {showForm ? (
                  <View style={styles.formWrap} testID="delivery.zone.form">
                    <Text style={styles.formTitle}>
                      {editing ? 'Editar zona' : 'Nueva zona'}
                    </Text>

                    <View style={styles.formField}>
                      <Text style={styles.formFieldLabel}>Nombre</Text>
                      <TextInput
                        style={styles.input}
                        value={draft.name}
                        onChangeText={(v) => setDraft({ ...draft, name: v })}
                        placeholder="Ej: Zona centro"
                        testID="delivery.zone.name"
                      />
                    </View>

                    <View style={styles.formField}>
                      <Text style={styles.formFieldLabel}>Tipo de zona</Text>
                      <View style={styles.typeRow}>
                        <TouchableOpacity
                          style={[
                            styles.typeBtn,
                            draft.zone_type === 'radius' && styles.typeBtnActive,
                          ]}
                          onPress={() => setDraft({ ...draft, zone_type: 'radius' })}
                          testID="delivery.zone.type-radius"
                        >
                          <Text style={styles.typeBtnText}>Radio</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.typeBtn, styles.typeBtnDisabled]}
                          disabled
                        >
                          <Text style={[styles.typeBtnText, { color: ticketColors.ink3 }]}>
                            Polígono (próximamente)
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {draft.zone_type === 'radius' ? (
                      <View style={styles.formField}>
                        <Text style={styles.formFieldLabel}>Radio (metros)</Text>
                        <TextInput
                          style={styles.input}
                          value={String(draft.radius_meters)}
                          onChangeText={(v) =>
                            setDraft({ ...draft, radius_meters: parseInt(v) || 0 })
                          }
                          keyboardType="numeric"
                          placeholder="1000"
                          testID="delivery.zone.radius"
                        />
                      </View>
                    ) : null}

                    <View style={styles.mapContainer}>
                      {mapRegion && branchLat && branchLng && canRenderNativeMapView() ? (
                        <MapView
                          style={styles.map}
                          initialRegion={mapRegion}
                          showsUserLocation={false}
                        >
                          <Marker coordinate={{ latitude: branchLat, longitude: branchLng }} />
                          {draft.zone_type === 'radius' && (
                            <Circle
                              center={{ latitude: branchLat, longitude: branchLng }}
                              radius={draft.radius_meters}
                              strokeColor={ticketColors.gold}
                              fillColor={`${ticketColors.gold}33`}
                            />
                          )}
                        </MapView>
                      ) : (
                        <View style={[styles.map, { alignItems: 'center', justifyContent: 'center' }]}>
                          <Text style={styles.mapPlaceholder}>
                            {mapLoadError
                              ? 'No se pudo cargar la ubicación de la sucursal.'
                              : canRenderNativeMapView()
                                ? 'Cargando mapa…'
                                : 'El mapa no está disponible en esta versión.'}
                          </Text>
                        </View>
                      )}
                    </View>

                    <View style={styles.formRow}>
                      {ownDeliveryEnabled ? (
                        <View style={styles.formFieldHalf}>
                          <Text style={styles.formFieldLabel}>Tarifa de envío ($)</Text>
                          <TextInput
                            style={styles.input}
                            value={String(draft.delivery_fee)}
                            editable={canMutateFees}
                            onChangeText={(v) => {
                              if (!canMutateFees) return;
                              setDraft({ ...draft, delivery_fee: parseFloat(v) || 0 });
                            }}
                            keyboardType="numeric"
                            placeholder="0"
                            testID="delivery.zone.fee"
                          />
                        </View>
                      ) : null}
                      <View style={styles.formFieldHalf}>
                        <Text style={styles.formFieldLabel}>Pedido mínimo ($)</Text>
                        <TextInput
                          style={styles.input}
                          value={String(draft.min_order_amount)}
                          editable={canMutateFees}
                          onChangeText={(v) => {
                            if (!canMutateFees) return;
                            setDraft({ ...draft, min_order_amount: parseFloat(v) || 0 });
                          }}
                          keyboardType="numeric"
                          placeholder="0"
                          testID="delivery.zone.min-order"
                        />
                      </View>
                    </View>

                    {vueltoRidersEnabled && !ownDeliveryEnabled ? (
                      <Text style={styles.hintCaption}>
                        Con repartidores independientes el precio sale según la distancia. No hace falta tarifa
                        plana.
                      </Text>
                    ) : null}
                    {vueltoRidersEnabled && ownDeliveryEnabled ? (
                      <Text style={styles.hintCaption}>
                        La tarifa plana es solo para el envío del local. Con repartidores independientes el precio
                        sale según la distancia.
                      </Text>
                    ) : null}

                    <View style={styles.formField}>
                      <Text style={styles.formFieldLabel}>Cuánto tarda (min)</Text>
                      <TextInput
                        style={styles.input}
                        value={String(draft.estimated_time_minutes)}
                        onChangeText={(v) =>
                          setDraft({ ...draft, estimated_time_minutes: parseInt(v) || 30 })
                        }
                        keyboardType="numeric"
                        placeholder="30"
                        testID="delivery.zone.eta"
                      />
                    </View>

                    <View style={styles.formActions}>
                      <View style={styles.flex1}>
                        <TicketButton
                          variant="ghost"
                          label="Cancelar"
                          onPress={() => setShowForm(false)}
                          testID="delivery.zone.cancel"
                        />
                      </View>
                      <View style={styles.flex1}>
                        <TicketButton
                          variant="gold"
                          label={saving ? 'Guardando…' : 'Guardar'}
                          onPress={handleSave}
                          disabled={saving}
                          testID="delivery.zone.save"
                        />
                      </View>
                    </View>
                  </View>
                ) : (
                  <View style={styles.addZoneWrap}>
                    <TicketButton
                      variant="ghost"
                      label="+ Agregar zona"
                      onPress={handleStartCreate}
                      testID="delivery.zone.add"
                    />
                  </View>
                )}
              </>
            )}
          </View>
        </PaperSheet>
      ) : null}
      {ConfirmModal}
    </View>
  );
}

const styles = StyleSheet.create({
  sectionStack: {
    gap: 10,
  },
  modesStack: {
    gap: spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  sectionCaption: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
  },
  toggleCopy: {
    flex: 1,
    gap: 2,
    paddingRight: spacing.sm,
  },
  toggleLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  toggleHint: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  hintWarn: {
    fontSize: 13,
    color: ticketColors.warn,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
  },
  hintCaption: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  zonesStack: {
    gap: spacing.md,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  loadingText: {
    fontSize: 14,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  errorRow: {
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  errorText: {
    fontSize: 14,
    color: ticketColors.bad,
    fontFamily: ticketFonts.sans,
  },
  retryBtn: {
    fontSize: 14,
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
  },
  zonesList: {
    gap: spacing.sm,
  },
  zoneCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  zoneInfo: {
    flex: 1,
    gap: 2,
  },
  zoneName: {
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  zoneMeta: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 16,
  },
  zoneActions: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  editBtn: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.gold,
    fontWeight: '600',
  },
  deleteBtn: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  formWrap: {
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  formField: {
    gap: spacing.xs,
  },
  formFieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  input: {
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  typeRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  typeBtn: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
  },
  typeBtnActive: {
    borderColor: ticketColors.gold,
    backgroundColor: `${ticketColors.gold}22`,
  },
  typeBtnDisabled: {
    opacity: 0.5,
  },
  typeBtnText: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  mapContainer: {
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  map: {
    width: '100%',
    height: 200,
  },
  mapPlaceholder: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  formFieldHalf: {
    flex: 1,
    gap: spacing.xs,
  },
  formActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  flex1: {
    flex: 1,
  },
  addZoneWrap: {
    marginTop: spacing.xs,
  },
});
